import Vue from 'vue'

const cacheStore = new Map()
const pendingStore = new Map()

function serializeKey(key) {
  return JSON.stringify(key)
}

/**
 * Membuat instance Vue untuk menangani pengambilan data secara cacheable.
 *
 * Fungsi ini mengembalikan objek Vue yang memiliki state dan method `refetch`
 * untuk melakukan pemanggilan data menggunakan fungsi `fetcher`.
 *
 * @param {string[]} keyArray - Array of keys untuk membuat key cache.
 * @param {function} fetcher - Fungsi async yang digunakan untuk mengambil data.
 * @param {object} options - Opsi untuk konfigurasi fetcher.
 * @param {number} [options.staleTime=0] - Waktu kadaluarsa data dalam milidetik.
 * @param {boolean} [options.immediate=true] - Apakah data akan diambil secara langsung saat component dibuat.
 *
 * @returns {object} Objek Vue dengan data reaktif dan method `refetch` untuk mengambil data secara cacheable.
 *
 * @example
 * const fetcher = useCacheableFetcher(['/komship/api/v1/marketplace-orders/', this.id, 'mitras'], this.fetchDetail, {
        immediate: false,
        staleTime: 1 * 60 * 1000,
      })
 * fetcher.refetch()
 *
 * @example
 * fetcher.invalidate()
 */
export default function useCacheableFetcher(keyArray, fetcher, options = {}) {
  const {
    staleTime = 0,
    immediate = true,
  } = options

  const key = serializeKey(keyArray)

  const state = Vue.observable({
    data: null,
    isLoading: false,
    error: null,
  })

  const execute = async () => {
    const now = Date.now()

    if (cacheStore.has(key)) {
      const { data: cachedData, timestamp } = cacheStore.get(key)

      if (now - timestamp < staleTime) {
        state.data = cachedData
        return cachedData
      }
    }

    if (pendingStore.has(key)) {
      return pendingStore.get(key)
    }

    state.isLoading = true
    state.error = null

    const promise = fetcher()
      .then(result => {
        cacheStore.set(key, {
          data: result,
          timestamp: Date.now(),
        })

        state.data = result
        return result
      })
      .catch(err => {
        state.error = err
      })
      .finally(() => {
        state.isLoading = false
        pendingStore.delete(key)
      })

    pendingStore.set(key, promise)

    return promise
  }

  const invalidate = () => {
    cacheStore.delete(key)
  }

  if (immediate) {
    execute()
  }

  return {
    state,
    refetch: execute,
    invalidate,
  }
}

/**
 * Invalidate the cache for the given key array
 * @param keyArray {string[]} - The key array to invalidate
 * @returns {void}
 */
export function invalidateQuery(keyArray) {
  const prefixArray = Array.isArray(keyArray) ? keyArray : [keyArray]

  cacheStore.forEach((_, key) => {
    try {
      const parsedKey = JSON.parse(key)

      if (
        Array.isArray(parsedKey)
        && parsedKey.length >= prefixArray.length
        && prefixArray.every((val, idx) => parsedKey[idx] === val)
      ) {
        cacheStore.delete(key)
      }
    } catch (e) {
      // Abaikan key yang tidak bisa diparse sebagai JSON array
    }
  })
}
